import i18n from 'i18n';

/**
 * Translating backend zod validation errors (docs/features/server-zod-i18n/).
 *
 * The API is language-neutral: every zod failure travels as
 * `{ code, path, message, params }` — `issues[]` on regular 400 bodies,
 * per-row `fieldIssues` on upload endpoints. We translate by `code` in the
 * CURRENT locale; the English `message` is the render fallback, never an API:
 * do not parse or string-match it, and take field labels from `path`.
 *
 * Sibling of validationMessages.js and built the same way: global `i18n.t`
 * so non-React callers work, and every key comment-declared below so the
 * extractor (which cannot see `'serverErrors.zod.' + code`) harvests the
 * English text into en/common.json and removeUnusedKeys does not prune it.
 *
 * The backend registry (SCREAMING_SNAKE codes) is APPEND-ONLY — a shipped
 * code never changes meaning; backend semantic changes arrive as new codes.
 * zod's own built-in codes (lower snake_case) share the namespace and cannot
 * collide. `custom` — a custom issue without a registry code — deliberately
 * has NO catalogue entry: its `message` is a site-specific sentence that
 * generic copy must never override.
 */

const NS_PREFIX = 'serverErrors.zod.';

// Envelope codes that render as one generic "contact support" sentence —
// their distinction is diagnostic, not user-actionable (phase 6 D18/D24).
const GENERIC_CODES = new Set([
  'INTERNAL',
  'SERVICE_UNAVAILABLE',
  'BAD_REQUEST',
  'DB_VALIDATION',
  'GONE',
  'REFERENCE_DATA_MISSING',
]);

/** Collapse the double spaces optional interpolation slots leave behind. */
const tidy = (s) => (typeof s === 'string' ? s.replace(/\s{2,}/g, ' ').trim() : s);

/**
 * Pre-format wire params for interpolation: arrays join to prose lists,
 * `id`/`ids` collapse into one `ids` slot, `dependency` (an entity token)
 * and `orgNames` become display text. Scalars pass through.
 */
function formatParams(params = {}) {
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    out[k] = Array.isArray(v) ? v.join(', ') : v;
  }
  out.ids = out.id ?? out.ids ?? '';
  if (params.dependency) out.dependency = entityLabel(params.dependency);
  return out;
}

/**
 * Translate one wire issue in the active locale. Issue codes live in TWO
 * namespaces by design: zod-layer codes under `serverErrors.zod.*`, and
 * composite-validation issues reuse the envelope taxonomy
 * (`STATE_CONFLICT`/`CONSTRAINT`) under `serverErrors.*` — try zod first,
 * then the envelope namespace, then the wire `message`.
 *
 * @param {{ code?: string, message?: string, params?: Record<string, string|number|boolean> }} issue
 * @returns {string|undefined} the translated sentence, or the English
 *   `message` when the code is `custom`, unknown, or not yet translated.
 */
export function renderIssue(issue) {
  if (!issue || typeof issue !== 'object') return undefined;
  const { code, message, params } = issue;
  if (!code || code === 'custom') return message;
  const vars = { ...formatParams(params), defaultValue: message ?? code };
  if (i18n.exists(`${NS_PREFIX}${code}`)) return tidy(i18n.t(`${NS_PREFIX}${code}`, vars));
  if (i18n.exists(`serverErrors.${code}`, { count: vars.count })) {
    return tidy(i18n.t(`serverErrors.${code}`, { ...vars, entity: entityFallback() }));
  }
  return tidy(i18n.t(`${NS_PREFIX}${code}`, vars));
}

/** The translated domain noun for an `entity` wire token (30-value enum). */
export function entityLabel(entity) {
  if (!entity) return entity;
  const raw = String(entity);
  return i18n.t(`entities.${raw}`, {
    defaultValue: raw.charAt(0).toUpperCase() + raw.slice(1),
  });
}

const entityFallback = () => i18n.t('serverErrors.fallbackEntity', 'Record');

/**
 * The §1 resolution rule for the error envelope: translate by `code` with
 * the translated `entity` noun, `fieldName` and pre-formatted `params`
 * interpolated; the frozen English `message` is the fallback for unknown
 * codes. Versioned envelopes (`{ version: 1, … }`) are schema-pinned
 * contracts and are deliberately NOT resolved here (D23).
 *
 * @returns {string|undefined}
 */
export function renderServerError(data) {
  if (!data || typeof data !== 'object' || data.version != null) return undefined;
  const { code, message, entity, fieldName, params } = data;
  if (!code || typeof code !== 'string') return undefined;
  // String concat, not a literal: the extractor's call analysis would otherwise
  // register this key default-less and beat the comment declaration's English.
  const key = 'serverErrors.' + (GENERIC_CODES.has(code) ? 'generic' : code);
  const vars = {
    ...formatParams(params),
    entity: entity ? entityLabel(entity) : entityFallback(),
    field: fieldName || i18n.t('serverErrors.fallbackField', 'value'),
    ...(Array.isArray(data.orgNames) && { orgNames: data.orgNames.join(', ') }),
  };
  if (!i18n.exists(key, { count: vars.count }) && !message) return undefined;
  return tidy(i18n.t(key, { ...vars, defaultValue: message }));
}

/**
 * Requirements-gate checklist model (`code: REQUIREMENTS_NOT_MET` +
 * `blockers[]`). Presentation stays at the call site; `field` maps to the
 * fix-it screen where one exists.
 */
export function blockersOf(data) {
  const blockers = Array.isArray(data?.blockers) ? data.blockers.filter(Boolean) : [];
  return blockers.map((b) => ({
    field: b.field,
    code: b.code,
    requirement: b.requirement,
    text:
      renderIssue({
        code: b.code,
        message: b.message,
        params: { requirement: b.requirement, field: b.field },
      }) ??
      b.message ??
      b.requirement,
  }));
}

/**
 * AI failure model (`code: AI_*` + `retryable` + optional
 * `retryAfterSeconds`). UIs show a retry affordance only when `retryable`.
 */
export function aiErrorOf(data) {
  if (!data || typeof data.code !== 'string' || !data.code.startsWith('AI_')) return undefined;
  return {
    code: data.code,
    text: renderServerError(data) ?? data.message,
    retryable: !!data.retryable,
    retryAfterSeconds: data.retryAfterSeconds,
  };
}

/**
 * Success toasts (phase 7 D25): every 2xx with a user-visible `message` also
 * carries `code` (55-value serverSuccess enum). The 2026-08 audit found ZERO
 * sites echoing a server success message — every success toast uses local
 * translated copy (D26: those stay). This helper is the standing rule for
 * any future echo site; catalogue keys are added append-only when a site
 * first echoes (add a t-declaration comment for serverSuccess-dot-CODE in a
 * comment here, then fill the 7 locales — same workflow as the zod codes).
 */
export function successMessage(response, fallback) {
  const data = response?.data ?? response;
  if (!data || typeof data !== 'object') return fallback;
  if (typeof data.code === 'string' && data.code) {
    return tidy(
      i18n.t('serverSuccess.' + data.code, {
        ...formatParams(data.params),
        defaultValue: data.message ?? fallback,
      }),
    );
  }
  return data.message ?? fallback;
}

/**
 * Upload rows: translate the cell for `field` from `fieldIssues` when the
 * backend sent it, else return `fallback` (the legacy `fieldErrors` prose —
 * older deploys don't send `fieldIssues` at all).
 */
/**
 * Which column a server error is ABOUT, when the wire does not say.
 *
 * Upload refusals arrive from some endpoints as bare prose — UK confirm answers
 * `failedItems[].errors` as sentences (`docs/features/import-error-correction/
 * 00-OVERVIEW.md` E3) — and the import wizard needs a field to mark the cell,
 * count the column and offer a jump. Without one, "CN good does not exist"
 * renders as an unplaceable sentence over a grid whose CN Code column is the
 * only thing it could possibly mean.
 *
 * But the code DOES say. `CN_GOOD_NOT_FOUND` is about the CN good, always,
 * whichever document is being imported — so this is a fact about the server's
 * vocabulary, which is what this module is for, and one table serves every
 * wizard rather than each flow guessing for itself.
 *
 * Values are in the BACKEND's field vocabulary, so the wizard's existing
 * backend→column translation (`BACKEND_TO_FRONTEND_FIELD`) does the last hop
 * and there is no second naming scheme to keep in step.
 *
 * Deliberately small: only codes whose subject is unambiguous — or, as with
 * `COUNTRY_OF_ORIGIN_REQUIRED`, whose one always-editable remedy is. Any other
 * code that could be about more than one column belongs to whichever field the
 * server names explicitly, not to a guess made here.
 */
const FIELD_BY_SERVER_CODE = {
  CN_GOOD_INVALID: 'cnGood',
  CN_GOOD_NOT_FOUND: 'cnGood',
  CN_CODE_NOT_FOUND: 'cnGood',
  CN_CODE_NOT_IN_DATABASE: 'cnGood',
  CN_GOOD_ID_INVALID: 'cnGood',
  COUNTRY_INVALID: 'countryOfOrigin',
  EU_COUNTRY_INVALID: 'countryOfOrigin',
  CBAM_COUNTRY_INVALID: 'countryOfOrigin',
  UK_ORIGIN_EXCLUDED: 'countryOfOrigin',
  // Supplying an installation also clears it, but the origin cell is the one
  // the user can always fill in the grid.
  COUNTRY_OF_ORIGIN_REQUIRED: 'countryOfOrigin',
  EORI_ALREADY_EXISTS: 'EORI',
};

/**
 * The rendered sentence of every mapped code, in the language showing NOW.
 *
 * Rebuilt when the language changes rather than memoised once at import: these
 * are translated strings, and a table built in English would stop matching the
 * moment a user switched language — silently, and only for the anchoring.
 */
let reverseCache = { language: null, byText: null };

function codeByRenderedText() {
  const language = i18n.language;
  if (reverseCache.language === language && reverseCache.byText) return reverseCache.byText;

  const byText = new Map();
  for (const code of Object.keys(FIELD_BY_SERVER_CODE)) {
    const key = `${NS_PREFIX}${code}`;
    if (!i18n.exists(key)) continue;
    const text = tidy(i18n.t(key, { defaultValue: '' }));
    // A message with unfilled interpolation ("Invalid CN code: {{cnCode}} …")
    // never equals what the server rendered, and half-matching it would anchor
    // the wrong rows. Exact sentences only.
    if (!text || text.includes('{{')) continue;
    byText.set(text.toLowerCase(), code);
  }
  reverseCache = { language, byText };
  return byText;
}

/**
 * The field a refusal is about, from its code — or from its own words when the
 * server rendered them for us.
 *
 * @param {Object|string} entry An issue (`{ code }`) or the rendered sentence.
 * @returns {string|undefined} A BACKEND field name, or undefined when the code
 *   is unknown or the sentence is not one we can identify.
 */
export function serverIssueField(entry) {
  if (!entry) return undefined;
  if (typeof entry === 'object') {
    return entry.code ? FIELD_BY_SERVER_CODE[entry.code] : undefined;
  }
  if (typeof entry !== 'string') return undefined;
  const code = codeByRenderedText().get(tidy(entry).toLowerCase());
  return code ? FIELD_BY_SERVER_CODE[code] : undefined;
}

export function fieldIssueMessage(fieldIssues, field, fallback) {
  const issue = fieldIssues && typeof fieldIssues === 'object' ? fieldIssues[field] : undefined;
  return (issue && renderIssue(issue)) || fallback;
}

/** The structured issues of an axios error, or [] — never throws. */
export function issuesOf(error) {
  const issues = error?.response?.data?.issues;
  return Array.isArray(issues) ? issues.filter(Boolean) : [];
}

/**
 * Toast-title nouns. The backend derives `resource`/`action` from the URL and
 * HTTP method (errorHandler.ts) — a CLOSED set of English nouns, translated
 * here with the raw noun as fallback so an unknown/new noun degrades to
 * English, never to a raw key.
 */
const RESOURCE_PREFIX = 'serverErrors.resource.';
const ACTION_PREFIX = 'serverErrors.action.';

export function resourceLabel(resource) {
  if (!resource) return resource;
  return i18n.t(`${RESOURCE_PREFIX}${String(resource).toLowerCase()}`, { defaultValue: resource });
}

export function actionLabel(action) {
  if (!action) return action;
  return i18n.t(`${ACTION_PREFIX}${String(action).toLowerCase()}`, { defaultValue: action });
}

/**
 * Regular endpoints: the display string for an axios error, or `undefined`
 * when the response carries none — which makes this a drop-in replacement
 * for the `err?.response?.data?.message` token inside existing `||`/`??`
 * fallback chains (they keep deciding what to show when the server said
 * nothing).
 *
 * Prefers `issues[]` (translated); multiple issues join as
 * "field: sentence; field: sentence" with the field taken from `path`.
 */
export function apiErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return undefined;
  const issues = Array.isArray(data.issues) ? data.issues.filter(Boolean) : [];
  if (issues.length > 0) {
    const parts = issues.map((issue) => {
      const text = renderIssue(issue);
      const field = Array.isArray(issue.path) && issue.path.length > 0 ? issue.path.at(-1) : null;
      return issues.length > 1 && field ? `${field}: ${text}` : text;
    });
    return parts.join('; ');
  }
  // Envelope layer (phase 6): translate by the always-present `code`;
  // unknown code or versioned envelope falls through to the frozen message.
  return renderServerError(data) ?? data.message ?? undefined;
}

// ---------------------------------------------------------------------------
// Catalogue declarations. The extractor scans these comments; the English
// text is the backend's own message for that code (byte-equal modulo
// `{{param}}` slots), so a translated key and the untranslated fallback read
// identically in English. Params listed are what the backend interpolates.
//
// Backend registry: src/types/zodIssueCodes.ts on backend PR #312.
//
// --- registry codes (SCREAMING_SNAKE, append-only) -------------------------
// t('serverErrors.zod.PHONE_NUMBER_INVALID', 'Invalid phone number')
// t('serverErrors.zod.STRING_TOO_LONG', 'String length should be less than {{maxLength}}')
// t('serverErrors.zod.NUMBER_INVALID', 'Invalid number')
// t('serverErrors.zod.NUMBER_MUST_BE_VALID', 'Must be a valid number')
// t('serverErrors.zod.NUMBER_OUT_OF_RANGE', 'Must be between {{min}} and {{max}}')
// t('serverErrors.zod.NUMBER_MUST_BE_GTE', 'Must be greater than or equal to {{min}}')
// t('serverErrors.zod.NUMBER_MUST_BE_LTE', 'Must be less than or equal to {{max}}')
// t('serverErrors.zod.OBJECT_ID_INVALID', 'Invalid ObjectId')
// t('serverErrors.zod.COUNTRY_INVALID', 'Invalid country')
// t('serverErrors.zod.EU_COUNTRY_INVALID', 'Invalid EU country')
// t('serverErrors.zod.CBAM_COUNTRY_INVALID', 'Invalid CBAM country {{country}}')
// t('serverErrors.zod.DATE_INVALID', 'Invalid date')
// t('serverErrors.zod.FROM_DATE_INVALID', "Invalid 'from' date")
// t('serverErrors.zod.TO_DATE_INVALID', "Invalid 'to' date")
// t('serverErrors.zod.UK_ORIGIN_EXCLUDED', 'UK-origin goods are outside UK CBAM.')
// t('serverErrors.zod.FIELD_REQUIRED', '{{field}} is required')
// t('serverErrors.zod.CN_GOOD_INVALID', 'Invalid CN Good')
// t('serverErrors.zod.CN_GOOD_NOT_FOUND', 'CN good does not exist')
// t('serverErrors.zod.CN_CODE_NOT_FOUND', 'CN code does not exist')
// t('serverErrors.zod.CN_CODE_NOT_IN_DATABASE', 'Invalid CN code: {{cnCode}} - CN Good not found in database')
// t('serverErrors.zod.CN_GOOD_ID_INVALID', 'Invalid CN Good ID - good does not exist')
// t('serverErrors.zod.CN_GOOD_IDS_INVALID', 'One or more CN Good IDs are invalid')
// t('serverErrors.zod.CN_GOOD_IDS_DUPLICATE', 'Duplicate CN Good IDs are not allowed')
// t('serverErrors.zod.ORG_ID_REQUIRED', 'At least one of the declarant ID and operator ID need to exist')
// t('serverErrors.zod.ORG_ID_REQUIRED_WITH_INSTALLATION', 'declarantId or operatorId is required when installationId is provided')
// t('serverErrors.zod.DECLARANT_ID_REQUIRED_WITH_SUPPLIER', 'declarantId is required when supplierId is provided')
// t('serverErrors.zod.ORGANIZATION_ALREADY_EXISTS', 'User already has an organization. Cannot create duplicate.')
// t('serverErrors.zod.ORGANIZATION_NAME_INVALID_WORDS', 'Organization name contains invalid words')
// t('serverErrors.zod.ROLE_OTHER_DETAILS_REQUIRED', "Please specify your role when selecting 'Other'")
// t('serverErrors.zod.REFERRAL_OTHER_DETAILS_REQUIRED', "Please specify how you heard about us when selecting 'Other'")
// t('serverErrors.zod.EORI_ALREADY_EXISTS', 'EORI already exists')
// t('serverErrors.zod.EMAIL_DOMAIN_GENERIC', 'Invalid email, you must use organization email domains and not generic domains such as {{emailDomain}}')
// t('serverErrors.zod.SUPPLIER_ID_INVALID', 'Invalid supplier ID {{supplierId}}')
// t('serverErrors.zod.SUPPLIER_ID_REQUIRED_FOR_RESOLUTION', "supplierId is required unless efResolution is 'template-actual'")
// t('serverErrors.zod.SUPPLIER_NOT_ACCESSIBLE', 'Invalid supplier ID or supplier not accessible to operator')
// t('serverErrors.zod.SUPPLIER_NOT_FOUND_OR_INACCESSIBLE', 'Supplier with ID {{supplierId}} not found or not accessible')
// t('serverErrors.zod.SUPPLIER_NOT_RELATED_TO_INSTALLATION', 'Supplier with ID {{supplierId}} is not related to installation with ID {{installationId}}')
// t('serverErrors.zod.SUPPLIER_INTERNAL_ID_EXISTS', 'Supplier with internal ID "{{internalId}}" already exists')
// t('serverErrors.zod.SUPPLIER_ALREADY_CONNECTED', 'Already connected with this supplier')
// t('serverErrors.zod.CONNECTION_ALREADY_SENT', 'Already sent this connection')
// t('serverErrors.zod.POTENTIAL_SUPPLIER_EXISTS', 'Potential supplier with this name and country already exists')
// t('serverErrors.zod.ACTUAL_SUPPLIER_ID_REQUIRED', 'actualSupplierId is required')
// t('serverErrors.zod.INSTALLATION_ID_INVALID', 'Invalid installation ID {{installationId}}')
// t('serverErrors.zod.INSTALLATION_NOT_ACCESSIBLE', 'Invalid installation ID or installation not accessible to operator')
// t('serverErrors.zod.INSTALLATION_NOT_FOUND_OR_INACCESSIBLE', 'Installation with ID {{installationId}} and supplier ID {{supplierId}} not found or not accessible')
// t('serverErrors.zod.INSTALLATION_NOT_FOUND_FOR_OPERATOR', 'Installation not found for this operator')
// t('serverErrors.zod.INSTALLATION_ON_CONNECTED_SUPPLIER', 'Cannot add or change installations of the connected supplier {{supplierName}}')
// t('serverErrors.zod.INSTALLATION_INTERNAL_ID_EXISTS', 'Installation with internal ID: {{internalId}} already exists')
// t('serverErrors.zod.INSTALLATION_OR_SUPPLIER_REQUIRED', 'Either installationID or supplierID must be provided')
// t('serverErrors.zod.OPERATOR_ID_INVALID', 'Invalid operatorId')
// t('serverErrors.zod.COUNTRY_ID_INVALID', 'Invalid countryId')
// t('serverErrors.zod.EF_SOURCE_VALUE_REQUIRED', 'Emission factor source value is required')
// t('serverErrors.zod.EF_SOURCE_VALUE_MISSING_FOR_CN', 'Emission factor source value is missing for CN code {{cnCode}}')
// t('serverErrors.zod.EMISSIONS_FACTOR_OVERLAPS', 'Overlapping emissions factor')
// t('serverErrors.zod.EMISSION_FACTOR_INVALID', 'Invalid emission factor')
// t('serverErrors.zod.EMISSION_VALUE_REQUIRED', 'At least one emission value (direct, indirect, or total) must be provided')
// t('serverErrors.zod.REPORTING_PERIOD_END_BEFORE_START', '{{entityId}} - {{cnGood}} - Reporting period end date ({{to}}) must be after the start date ({{from}})')
// t('serverErrors.zod.INDIRECT_EMISSIONS_MISMATCH', '{{entityId}} - {{cnGood}} - Electricity consumed ({{electricityConsumed}}) × electricity factor ({{electricityFactor}}) is not equal to indirect embedded emissions ({{indirectEmbedded}}) (rounded to 7 decimal places)')
// t('serverErrors.zod.PRODUCTION_METHOD_ID_INVALID', 'Invalid production method id: {{productionMethodId}}')
// t('serverErrors.zod.EMISSION_DATA_PROCESSING_ERROR', 'Error processing emission data: {{error}}')
// t('serverErrors.zod.INWARD_PROCESSING_INFO_REQUIRED', 'Inwards processing information is required')
// t('serverErrors.zod.GLOBALISATION_TIME_ORDER_INVALID', 'Globalisation time start must be before globalisation time end')
// t('serverErrors.zod.CONSIGNMENT_ID_EXISTS', 'Shipment with this Consignment ID already exists')
// t('serverErrors.zod.REPRESENTATIVE_COMPANY_REQUIRED', 'Company is required for representative')
// t('serverErrors.zod.COMPANY_ID_INVALID', 'Invalid company with ID {{companyId}}')
// t('serverErrors.zod.COUNTRY_OF_ORIGIN_REQUIRED', 'Country of origin is required when no installation is provided')
// t('serverErrors.zod.COUNTRY_OF_ORIGIN_MISMATCH', "Country of origin does not match the installation's country")
// t('serverErrors.zod.YEAR_OUT_OF_RANGE', 'Year must be between {{min}} and {{max}}')
//
// --- Template Issues (backend src/utils/templates/templateIssues.ts, append-only).
// --- What an EFDA / Communication Template parse could not read or had to
// --- assume. Copy is frontend-owned: it names the row and cell the way the
// --- user sees them in Excel. Rendered by modules/Common/templateIssues.
// t('serverErrors.zod.TEMPLATE_SHEET_MISSING', 'The sheet "{{sheet}}" is missing from this workbook.')
// t('serverErrors.zod.TEMPLATE_SHEET_EMPTY', 'The sheet "{{sheet}}" is empty.')
// t('serverErrors.zod.TEMPLATE_VERSION_MISSING', 'The template version could not be found near the top of the report sheet.')
// t('serverErrors.zod.TEMPLATE_REPORTING_PERIOD_MISSING', 'The reporting period is missing (cells {{cells}}).')
// t('serverErrors.zod.TEMPLATE_REPORTING_PERIOD_INVALID', 'The reporting period could not be read (cells {{cells}}: {{value}}).')
// t('serverErrors.zod.TEMPLATE_NO_GOODS_ROWS', 'No goods rows could be read from this template.')
// t('serverErrors.zod.TEMPLATE_ROW_VALUE_NOT_NUMERIC', 'Row {{row}}: "{{value}}" is not a number ({{field}}).')
// t('serverErrors.zod.TEMPLATE_ROW_CN_CODE_INCOMPLETE', 'Row {{row}}: the CN code "{{value}}" is incomplete; 8 digits are required.')
// t('serverErrors.zod.TEMPLATE_ROW_PRODUCTION_METHOD_UNKNOWN', 'Row {{row}}: no production method is known for CN code {{cnCode}}.')
// t('serverErrors.zod.TEMPLATE_ROW_QUALIFYING_PARAMETER_INVALID', 'Row {{row}}: a qualifying parameter is invalid. {{detail}}')
// t('serverErrors.zod.TEMPLATE_ROW_UNREADABLE', 'Row {{row}} could not be read.')
// t('serverErrors.zod.TEMPLATE_ROW_VALUES_DEFAULTED', 'Row {{row}}: blank cells were read as 0 ({{fields}}).')
// t('serverErrors.zod.TEMPLATE_ROW_ELECTRICITY_SOURCE_DEFAULTED', 'Row {{row}}: the electricity source "{{value}}" was not recognised and was read as {{default}}.')
// t('serverErrors.zod.TEMPLATE_ROW_CN_CODE_UNKNOWN', 'CN code {{cnCode}} is not in our reference data, so its goods category is unknown.')
// t('serverErrors.zod.TEMPLATE_DIAGRAM_NOT_BUILT', 'The production diagram could not be built from this template. Emissions can still be saved without it.')
// t('serverErrors.zod.TEMPLATE_DIAGRAM_ROW_SKIPPED', 'Left out of the diagram: {{detail}}.')
// t('serverErrors.zod.TEMPLATE_ROW_INDIRECT_MISMATCH', 'Row {{row}}: indirect emissions are stated as {{stated}}, but electricity consumed × emission factor ({{consumed}} × {{factor}}) gives {{calculated}}.')
// t('serverErrors.zod.TEMPLATE_ROW_DUPLICATE_CONFLICT', 'Rows {{rows}} give different figures for the same good (CN code {{cnCode}}).')
// t('serverErrors.zod.IMPORT_YEAR_OUT_OF_RANGE', 'Year of import must be between {{min}} and {{max}}')
// t('serverErrors.zod.PURCHASE_ORDER_INVOICE_ID_EXISTS', 'Purchase order with invoice ID "{{invoiceId}}" already exists')
// t('serverErrors.zod.UK_REGISTRATION_DATE_IN_FUTURE', 'The registration date cannot be after today')
// t('serverErrors.zod.HEDGE_CLOSE_BEFORE_TRADE', 'Close the hedge on or after the day it is entered')
// t('serverErrors.zod.HEDGE_CLOSE_AFTER_SURRENDER', 'Close the hedge by the {{year}} surrender, {{surrender}}')
// t('serverErrors.zod.HEDGE_FIELD_WRONG_INSTRUMENT', 'This field does not apply to this instrument')
// t('serverErrors.zod.PLANNING_PART_NOT_FOUND', 'This saved part no longer exists or is not yours')
// t('serverErrors.zod.PLANNING_PART_YEAR_MISMATCH', 'This saved part is for another certificate year')
// t('serverErrors.zod.SALES_ORDER_INVOICE_ID_EXISTS', 'Sales order with invoice ID "{{invoiceId}}" already exists')
// t('serverErrors.zod.DIAGRAM_PERIOD_OVERLAPS', 'Diagram reporting period overlaps with existing diagrams')
// t('serverErrors.zod.REPORT_UPDATE_EMPTY', 'Provide at least one of data, status, or refreshExtracts')
// t('serverErrors.zod.SECTION_IDS_INVALID', "sectionIds must be null (full report) or ['productionProcesses']")
// t('serverErrors.zod.OPERATOR_LOGO_URL_INVALID', 'operatorLogoUrl must be a base64 data: image or an https URL on the allowlist')
// t('serverErrors.zod.NAME_OR_DESCRIPTION_REQUIRED', 'At least one of `name` or `description` must be provided')
// t('serverErrors.zod.PERMISSION_SLUG_INVALID', 'Invalid permission slug — expected resource:action (lowercase letters, digits, underscores)')
// t('serverErrors.zod.UNEXPECTED', 'Unexpected error occurred')
//
// --- envelope taxonomy (phase 6 — the 12 param-carrying families first,
// --- then singles; copy is frontend-owned, the wire message is per-site) ---
// t('serverErrors.NOT_FOUND', '{{entity}} {{ids}} not found')
// t('serverErrors.NOT_ACCESSIBLE', '{{entity}} {{ids}} is not accessible from your organization')
// t('serverErrors.DUPLICATE_one', '{{entity}} {{ids}} already exists')
// t('serverErrors.DUPLICATE_other', '{{count}} duplicates already exist: {{ids}}')
// t('serverErrors.INVALID_FORMAT', 'Invalid format for {{field}}')
// t('serverErrors.MISSING_REQUIRED', '{{field}} is required')
// t('serverErrors.CONSTRAINT', '{{field}} is outside the allowed bounds')
// t('serverErrors.STATE_CONFLICT', 'This action is not possible in the current state')
// t('serverErrors.IMMUTABLE', '{{field}} cannot be changed once set')
// t('serverErrors.BLOCKED_BY_DEPENDENCY_one', 'Cannot proceed — a {{dependency}} still references this {{entity}}')
// t('serverErrors.BLOCKED_BY_DEPENDENCY_other', 'Cannot proceed — {{count}} {{dependency}} records still reference this {{entity}}')
// t('serverErrors.NOT_PERMITTED', 'You are not permitted to do this')
// t('serverErrors.LIMIT_REACHED', 'Plan limit reached ({{current}}/{{limit}})')
// t('serverErrors.VALIDATION', 'Validation failed — please check the highlighted fields')
// t('serverErrors.VERIFIER_EMAIL_PERSONAL', "Use the verifier's work email. Personal addresses (Gmail, Outlook, Yahoo, Zoho…) cannot be invited.")
// t('serverErrors.EXPIRY_IN_PAST', 'Pick an access end date in the future')
// t('serverErrors.UNAUTHENTICATED', 'You are not signed in')
// t('serverErrors.FORBIDDEN', "You don't have permission to do this")
// t('serverErrors.PLAN_EXPIRED', 'Your plan has expired')
// t('serverErrors.TRIAL_EXPIRED', 'Your trial has expired')
// t('serverErrors.NO_ACTIVE_PLAN', 'This organization has no active plan')
// t('serverErrors.ORG_ALREADY_EXISTS', 'This organization already exists')
// t('serverErrors.CROSS_ORG_USAGE', '{{entity}} is already in use by another organization: {{orgNames}}')
// t('serverErrors.CONFLICT', 'This change conflicts with the current data')
// t('serverErrors.OPTIMISTIC_CONCURRENCY_CONFLICT', 'Someone else changed this while you were editing — reload and try again')
// t('serverErrors.REQUIREMENTS_NOT_MET', 'Some requirements are not met yet')
// t('serverErrors.SUPPLIER_CONNECTED_DELETE_UNCONFIRMED', 'This supplier is connected. Confirm to delete it with the data it has published.')
// t('serverErrors.SUPPLIER_DATA_LOCKED', 'This supplier is connected: its installations, emissions and documents are managed in its own workspace.')
// t('serverErrors.SUPPLIER_PUBLICATION_REQUIREMENTS_NOT_MET', 'The supplier publication requirements are not met yet')
// t('serverErrors.MONITORING_PLAN_FINALIZATION_REQUIREMENTS_NOT_MET', 'This plan still has items to answer before it can be finalized.')
// t('serverErrors.RATE_LIMITED', 'Too many requests — wait a moment and try again')
// t('serverErrors.REGIME_UNSUPPORTED', "This feature is not available for your organisation's CBAM regime yet")
// t('serverErrors.FILE_TOO_LARGE', 'The file is too large')
// t('serverErrors.FILE_UPLOAD_ERROR', 'The file upload failed')
// t('serverErrors.generic', 'Something went wrong. If this keeps happening, contact support.')
// t('serverErrors.fallbackEntity', 'Record')
// t('serverErrors.fallbackField', 'value')
//
// --- AI failure codes (separate backend namespace, same key family;
// --- `retryable`/`retryAfterSeconds` drive the retry affordance) -----------
// t('serverErrors.AI_ABORTED', 'The AI run was cancelled')
// t('serverErrors.AI_INVALID_OUTPUT', 'The AI returned an unusable result — try again')
// t('serverErrors.AI_MODEL_INCOMPATIBLE', 'The configured AI model is not compatible with this feature')
// t('serverErrors.AI_NOT_CONFIGURED', 'AI is not configured for this organization')
// t('serverErrors.AI_PROVIDER_UNAVAILABLE', 'The AI provider is temporarily unavailable')
// t('serverErrors.AI_QUOTA_EXCEEDED', 'The AI quota has been used up')
// t('serverErrors.AI_REFUSED', 'The AI declined to process this content')
// t('serverErrors.AI_RUN_SUPERSEDED', 'A newer AI run replaced this one')
// t('serverErrors.AI_STEP_LIMIT_REACHED', 'The AI run hit its step limit before finishing')
// t('serverErrors.AI_TOOL_DENIED', 'The AI was not allowed to use a required tool')
// t('serverErrors.AI_TOOL_INPUT_INVALID', 'The AI produced invalid tool input — try again')
// t('serverErrors.AI_UNKNOWN_MODEL', 'An unknown AI model is configured')
// t('serverErrors.AI_UNSUPPORTED_DOCUMENT', 'This document type is not supported by the AI')
//
// --- entities.* (30 wire tokens — never render raw; tokens may contain
// --- spaces and that is fine as a key segment) -----------------------------
// t('entities.supplier', 'Supplier')
// t('entities.installation', 'Installation')
// t('entities.declarant', 'Declarant')
// t('entities.operator', 'Operator')
// t('entities.consultant', 'Consultant')
// t('entities.shipment', 'Shipment')
// t('entities.diagram', 'Diagram')
// t('entities.emission factor', 'Emission factor')
// t('entities.company', 'Company')
// t('entities.report', 'Report')
// t('entities.data request', 'Data request')
// t('entities.sell order', 'Sell order')
// t('entities.purchase order', 'Purchase order')
// t('entities.inventory lot', 'Inventory lot')
// t('entities.cn good', 'CN good')
// t('entities.country', 'Country')
// t('entities.plan', 'Plan')
// t('entities.checklist', 'Checklist')
// t('entities.quote', 'Quote')
// t('entities.benchmark', 'Benchmark')
// t('entities.potential supplier', 'Potential supplier')
// t('entities.user', 'User')
// t('entities.organization', 'Organization')
// t('entities.invitation', 'Invitation')
// t('entities.document', 'Document')
// t('entities.template', 'Template')
// t('entities.carbon price', 'Carbon price')
// t('entities.verification', 'Verification')
// t('entities.connection', 'Connection')
// t('entities.exchange rate', 'Exchange rate')
//
// --- toast-title nouns (backend errorHandler.ts: resources array + method
// --- map — closed sets; the raw English noun is the render fallback) -------
// t('serverErrors.resource.installation', 'Installation')
// t('serverErrors.resource.emission', 'Emission')
// t('serverErrors.resource.supplier', 'Supplier')
// t('serverErrors.resource.shipment', 'Shipment')
// t('serverErrors.resource.report', 'Report')
// t('serverErrors.resource.declarant', 'Declarant')
// t('serverErrors.resource.resource', 'Resource')
// t('serverErrors.action.creation', 'Creation')
// t('serverErrors.action.edit', 'Edit')
// t('serverErrors.action.fetch', 'Fetch')
// t('serverErrors.action.deletion', 'Deletion')
//
// --- zod built-in codes (lower snake_case; scalar params only — array-valued
// --- zod fields like enum `options` are not forwarded by the backend) ------
// t('serverErrors.zod.invalid_type', 'Expected {{expected}}, received {{received}}')
// t('serverErrors.zod.too_small', 'Must be at least {{minimum}}')
// t('serverErrors.zod.too_big', 'Must be at most {{maximum}}')
// t('serverErrors.zod.invalid_string', 'Invalid {{validation}}')
// t('serverErrors.zod.invalid_enum_value', 'Invalid value: {{received}}')
// t('serverErrors.zod.invalid_date', 'Invalid date value')
// t('serverErrors.zod.invalid_union', 'Invalid input')
// t('serverErrors.zod.unrecognized_keys', 'Unrecognized fields in input')
